import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { BookOpen, ArrowLeft, Download, ExternalLink, Link2, Send, Loader2, User, Pencil, X, MapPin } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Loading, EmptyState } from "@/components/common/Loading";
import { RichTextView } from "@/components/forms/RichTextEditor";
import { SeashellRating } from "@/components/common/Seashell";
import { BookmarkButton } from "@/components/social/ProfileActions";
import { ReportMenu } from "@/components/social/ReportMenu";
import { ReactionBar } from "@/components/social/ReactionBar";
import { ShareResourceButton } from "@/components/social/ShareResourceDialog";
import { AskQuestionButton } from "@/components/social/AskQuestionDialog";
import { PublicQna } from "@/components/social/PublicQna";
import { useReferences, labelFor } from "@/hooks/useReferences";
import { timeAgo } from "@/lib/timeago";
import { MentionInput } from "@/components/social/MentionAutocomplete";
import { EmojiPickerButton } from "@/components/social/EmojiPickerButton";
import { renderTextWithMentions } from "@/lib/mentions";
import { AuthorLink, AuthorAvatar } from "@/components/social/AuthorLink";
import { useAuth } from "@/context/AuthContext";
import LockedView from "@/pages/public/LockedView";
import { trackAction, trackInternalLink, hostOf } from "@/lib/analytics";

function ResourceCommentRow({ c, onChanged, onRemove }) {
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(c.body);
  const [saving, setSaving] = useState(false);

  const react = async (emoji) => {
    try { const { data } = await api.post(`/resources/comments/${c.id}/react`, { emoji }); onChanged(data); }
    catch (e) { toast.error("Could not react"); }
  };
  const saveEdit = async () => {
    if (!editText.trim()) return;
    setSaving(true);
    try { const { data } = await api.put(`/resources/comments/${c.id}`, { body: editText.trim() }); onChanged(data); setEditing(false); }
    catch (e) { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  const a = c.author || {};
  return (
    <div id={`comment-${c.id}`} className="flex items-start gap-2.5" data-testid={`resource-comment-${c.id}`}>
      <AuthorAvatar card={a}>
        <Avatar className="h-8 w-8">
          {a.headshot_url && <AvatarImage src={fileUrl(a.headshot_url)} alt="" />}
          <AvatarFallback className="bg-secondary text-xs">{(a.display_name || "?")[0]}</AvatarFallback>
        </Avatar>
      </AuthorAvatar>
      <div className="min-w-0 flex-1">
        <div className="rounded-lg bg-secondary/60 px-3 py-2">
          <div className="flex items-center justify-between gap-2">
            <AuthorLink card={a} className="text-sm font-[560] text-foreground" testId={`resource-comment-author-${c.id}`} />
            <span className="text-xs text-muted-foreground">{timeAgo(c.created_at)}{c.edited ? " · edited" : ""}</span>
          </div>
          {editing ? (
            <div className="mt-1.5">
              <MentionInput value={editText} onChange={(v) => setEditText(v.slice(0, 1000))} maxLength={1000} onKeyDown={(e) => e.key === "Enter" && saveEdit()} testId={`resource-comment-edit-input-${c.id}`} />
              <div className="mt-1.5 flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => { setEditing(false); setEditText(c.body); }}><X className="mr-1 h-4 w-4" /> Cancel</Button>
                <Button size="sm" onClick={saveEdit} disabled={saving || !editText.trim()} data-testid={`resource-comment-edit-save-${c.id}`}>{saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null} Save</Button>
              </div>
            </div>
          ) : (
            <p className="mt-0.5 break-words text-sm text-foreground">{renderTextWithMentions(c.body, c.mentions)}</p>
          )}
        </div>
        {!editing && (
          <div className="mt-1 flex flex-wrap items-center gap-3 pl-1">
            <ReactionBar reactions={c.reactions} myReaction={c.my_reaction} onReact={react} testId={`resource-comment-react-${c.id}`} />
            {c.can_edit && <button onClick={() => setEditing(true)} className="text-xs font-[540] text-muted-foreground hover:text-primary" data-testid={`resource-comment-edit-${c.id}`}>Edit</button>}
            {c.can_delete && <button onClick={onRemove} className="text-xs font-[540] text-muted-foreground hover:text-destructive" data-testid={`resource-comment-delete-${c.id}`}>Delete</button>}
          </div>
        )}
      </div>
    </div>
  );
}

function Comments({ resourceId }) {
  const [items, setItems] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();
  const inputRef = useRef(null);
  const targetComment = params.get("comment");

  useEffect(() => {
    let active = true;
    (async () => {
      try { const { data } = await api.get(`/resources/${resourceId}/comments`); if (active) setItems(data); }
      catch (e) { if (active) setItems([]); }
    })();
    return () => { active = false; };
  }, [resourceId]);

  useEffect(() => {
    if (!targetComment || !items || !items.some((c) => c.id === targetComment)) return undefined;
    const t = setTimeout(() => {
      const el = document.getElementById(`comment-${targetComment}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("so-flash");
        setTimeout(() => el.classList.remove("so-flash"), 2900);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [targetComment, items]);

  const add = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { const { data } = await api.post(`/resources/${resourceId}/comments`, { body: text.trim() }); setItems((p) => [...(p || []), data]); setText(""); }
    catch (e) { toast.error("Could not comment"); } finally { setBusy(false); }
  };
  const update = (data) => setItems((p) => (p || []).map((c) => (c.id === data.id ? data : c)));
  const remove = async (id) => {
    try { await api.delete(`/resources/comments/${id}`); setItems((p) => p.filter((c) => c.id !== id)); }
    catch (e) { toast.error("Could not delete"); }
  };

  return (
    <Card className="border-0 p-6" data-testid="resource-comments">
      <h2 className="text-lg font-[640]">Discussion {items && items.length ? `(${items.length})` : ""}</h2>
      <div className="mt-4 flex items-center gap-2">
        <EmojiPickerButton targetRef={inputRef} value={text} onChange={setText} testId="resource-comment-emoji" />
        <MentionInput inputRef={inputRef} value={text} onChange={setText} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Share your thoughts…" testId="resource-comment-input" />
        <Button onClick={add} disabled={busy || !text.trim()} data-testid="resource-comment-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</Button>
      </div>
      <div className="mt-4 space-y-3">
        {(items || []).map((c) => (
          <ResourceCommentRow key={c.id} c={c} onChanged={update} onRemove={() => remove(c.id)} />
        ))}
        {items && items.length === 0 && <p className="text-sm text-muted-foreground">Be the first to comment.</p>}
      </div>
    </Card>
  );
}

export default function PublicResource() {
  const { slug } = useParams();
  const { references } = useReferences();
  const { user, loading } = useAuth();
  const [r, setR] = useState(undefined);

  useEffect(() => {
    if (loading || !user || !user.email_verified) return;
    let active = true;
    (async () => {
      try { const { data } = await api.get(`/resources/public/${slug}`); if (active) setR(data); }
      catch (e) { if (active) setR(null); }
    })();
    return () => { active = false; };
  }, [user, loading, slug]);

  const rate = async (value) => {
    try { const { data } = await api.post(`/resources/${r.id}/rate`, { value }); setR((s) => ({ ...s, rating_avg: data.rating_avg, rating_count: data.rating_count, my_rating: data.my_rating })); toast.success("Thanks for rating!"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not rate"); }
  };

  // Decision 7: the canonical public resource URL only — never a tracking, search or dashboard
  // parameter. Analytics is emitted after a successful copy and never blocks the copy itself.
  const copyLink = async () => {
    const url = `${window.location.origin}/resource/${r.slug || slug}`;
    let copied = false;
    try {
      await navigator.clipboard.writeText(url);
      copied = true;
    } catch {
      try {
        const field = document.createElement("textarea");
        field.value = url;
        field.setAttribute("readonly", "");
        field.style.position = "fixed";
        field.style.opacity = "0";
        document.body.appendChild(field);
        field.select();
        copied = document.execCommand("copy");
        document.body.removeChild(field);
      } catch { copied = false; }
    }
    if (!copied) { toast.error("Could not copy the link — copy it from the address bar instead."); return; }
    toast.success("Link copied");
    trackAction("resource_copy_link_clicked", { entityType: "resource", entityId: r.id });
  };

  if (loading) return <div className="py-20"><Loading /></div>;
  if (!user || !user.email_verified) return <LockedView what="this resource" />;
  if (r === undefined) return <div className="py-20"><Loading /></div>;
  if (r === null) return (
    <div className="mx-auto max-w-3xl px-4 py-20">
      <EmptyState icon={BookOpen} title="Resource not found" description="This resource may have been removed or isn't published." action={<Link to="/resources"><Button>Browse resources</Button></Link>} />
    </div>
  );

  const place = [r.location?.city, r.location?.state, r.location?.country].filter(Boolean).join(", ");
  const files = (r.files && r.files.length ? r.files : (r.file && r.file.url ? [r.file] : [])).filter((f) => f && f.url);
  const topics = r.topics || [];
  const industries = r.industries || [];
  const classifications = r.business_classifications || [];
  const hasDetails = Boolean(place || topics.length || industries.length || classifications.length);

  return (
    <div className="bg-background">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="mt-4 flex items-center justify-between gap-3">
          <Link to="/resources" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> All resources</Link>
          {r.is_owner && (
            <Link to={`/dashboard/resources/${r.id}`} data-testid="resource-edit-button">
              <Button size="sm" variant="secondary" className="shadow-sm"><Pencil className="mr-1.5 h-4 w-4" /> Edit resource</Button>
            </Link>
          )}
        </div>
        <div className="flex flex-col gap-6 py-6 lg:grid lg:grid-cols-3">
          {/* Main — `contents` on mobile so Discussion can be pushed to the very end */}
          <div className="contents lg:block lg:space-y-6 lg:col-span-2">
            <div className="aspect-[16/9] w-full overflow-hidden rounded-xl bg-gradient-to-r from-teal-100 to-teal-50 shadow-[0_8px_30px_-12px_rgba(16,24,40,0.25)]" data-testid="resource-cover">
              {r.cover_url && <img src={fileUrl(r.cover_url)} alt="" className="h-full w-full object-cover" />}
            </div>
            <div>
              {r.resource_type && (
                <div className="flex flex-wrap gap-1.5">
                  <Badge className="bg-teal-50 text-teal-700">{labelFor(references.resource_types, r.resource_type)}</Badge>
                </div>
              )}
              <h1 className="mt-3 text-3xl font-[720] tracking-[-0.02em] text-foreground" data-testid="resource-detail-title">{r.title}</h1>
              {r.author_name && <p className="mt-1 text-muted-foreground">by {r.author_name}</p>}
              {r.summary && <p className="mt-2 text-lg text-muted-foreground">{r.summary}</p>}
              <div className="mt-3"><SeashellRating value={r.rating_avg} readOnly showValue count={r.rating_count} /></div>
            </div>
            {r.description && r.description !== "<p></p>" && (
              <Card className="border-0 p-6"><h2 className="text-lg font-[640]">Overview</h2><div className="mt-3"><RichTextView html={r.description} className="prose-so-spacious" /></div></Card>
            )}
            {files.length > 0 && (
              <Card className="border-0 p-6" data-testid="resource-downloads">
                <h2 className="text-lg font-[640]">Downloads</h2>
                <div className="mt-3 space-y-2">
                  {files.map((f, i) => (
                    <a key={f.url || i} href={fileUrl(f.url)} target="_blank" rel="noreferrer" download className="flex items-center gap-3 rounded-lg border border-border bg-secondary/40 px-3 py-2 transition-colors hover:bg-secondary" data-testid={`resource-file-${i}`}>
                      <Download className="h-4 w-4 text-primary" />
                      <span className="flex-1 truncate text-sm text-foreground">{f.name || "Attachment"}</span>
                      <ExternalLink className="h-4 w-4 text-muted-foreground" />
                    </a>
                  ))}
                </div>
              </Card>
            )}
            <div className="order-last lg:order-none" data-testid="resource-discussion-section">
              <Comments resourceId={r.id} />
            </div>
          </div>

          {/* Side */}
          <div className="space-y-4">
            <Card className="relative border-0 p-6">
              <div className="absolute right-2 top-2 z-20">
                <ReportMenu contentType="resource" contentId={r.id} ownerUserId={r.owner_id} isOwner={r.is_owner} className="h-8 w-8 rounded-full border border-border/60 bg-card/80 shadow-sm backdrop-blur-sm hover:bg-card" testId="report-menu-resource" />
              </div>
              <div className="space-y-2 pt-6">
                <ShareResourceButton resourceId={r.id} />
                <Button variant="secondary" className="w-full" onClick={copyLink}
                        aria-label="Copy link to this resource" data-testid="resource-copy-link">
                  <Link2 className="mr-2 h-4 w-4" aria-hidden="true" /> Copy link
                </Button>
                {r.external_url && (
                  <a
                    href={r.external_url}
                    target="_blank"
                    rel="noreferrer"
                    className="block"
                    onClick={() => trackAction("resource_visit_clicked", {
                      entityType: "resource",
                      entityId: r.id,
                      meta: { destination_host: hostOf(r.external_url) },
                    })}
                  >
                    <Button variant="secondary" className="w-full" data-testid="resource-external"><ExternalLink className="mr-2 h-4 w-4" /> Visit resource</Button>
                  </a>
                )}
                <BookmarkButton itemType="resource" itemId={r.id} className="w-full justify-center" />
              </div>
            </Card>

            {hasDetails && (
              <Card className="border-0 p-6" data-testid="resource-details-card">
                {place && (
                  <div className="flex items-start gap-2 text-sm text-foreground" data-testid="resource-location">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>{place}</span>
                  </div>
                )}
                {topics.length > 0 && (
                  <div className={place ? "mt-5" : ""}>
                    <h3 className="text-sm font-[640] uppercase tracking-wide text-muted-foreground">Topics</h3>
                    <div className="mt-2 flex flex-wrap gap-1.5">{topics.map((t) => <Badge key={t} variant="secondary">{labelFor(references.resource_topics, t)}</Badge>)}</div>
                  </div>
                )}
                {industries.length > 0 && (
                  <div className={(place || topics.length) ? "mt-5" : ""}>
                    <h3 className="text-sm font-[640] uppercase tracking-wide text-muted-foreground">Industries</h3>
                    <div className="mt-2 flex flex-wrap gap-1.5">{industries.map((t) => <Badge key={t} className="bg-teal-50 text-teal-700">{labelFor(references.industries, t)}</Badge>)}</div>
                  </div>
                )}
                {classifications.length > 0 && (
                  <div className={(place || topics.length || industries.length) ? "mt-5" : ""}>
                    <h3 className="text-sm font-[640] uppercase tracking-wide text-muted-foreground">Classifications</h3>
                    <div className="mt-2 flex flex-wrap gap-1.5">{classifications.map((t) => <Badge key={t} variant="secondary">{labelFor(references.business_classifications, t)}</Badge>)}</div>
                  </div>
                )}
              </Card>
            )}

            {!r.is_owner && (
              <Card className="border-0 p-6">
                <h3 className="text-sm font-[640] uppercase tracking-wide text-muted-foreground">Rate this resource</h3>
                <p className="mt-1 text-xs text-muted-foreground">Tap a seashell to rate.</p>
                <div className="mt-3"><SeashellRating value={r.my_rating || 0} onRate={rate} size="h-7 w-7" testIdPrefix="seashell-rate" /></div>
                {r.my_rating ? <p className="mt-2 text-xs text-muted-foreground">You rated this {r.my_rating}/5.</p> : null}
              </Card>
            )}

            <Card className="border-0 p-6">
              <h3 className="text-sm font-[640] uppercase tracking-wide text-muted-foreground">Shared by</h3>
              <div className="mt-3 flex items-center gap-3">
                <Avatar className="h-10 w-10">
                  {r.author?.headshot_url && <AvatarImage src={fileUrl(r.author.headshot_url)} alt="" />}
                  <AvatarFallback className="bg-teal-50 text-teal-700"><User className="h-4 w-4" /></AvatarFallback>
                </Avatar>
                <div className="text-sm">
                  <div className="font-[600] text-foreground">{r.author ? r.author.display_name : "Member"}</div>
                  {r.author?.slug && <Link to={`/p/${r.author.slug}`} className="text-primary hover:underline">View profile</Link>}
                </div>
              </div>
            </Card>

            {r.organizer && (
              <Card className="border-0 p-6">
                <h3 className="text-sm font-[640] uppercase tracking-wide text-muted-foreground">Contributed by</h3>
                <div className="mt-3 flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden bg-secondary">
                    {r.organizer.profile_image_url ? <img src={fileUrl(r.organizer.profile_image_url)} alt="" className="h-full w-full object-cover" /> : <span className="font-[600] text-muted-foreground">{(r.organizer.display_name || "?")[0]}</span>}
                  </div>
                  <div className="text-sm">
                    <div className="font-[600] text-foreground">{r.organizer.display_name}</div>
                    {r.organizer.slug && (
                      <Link
                        to={`/b/${r.organizer.slug}`}
                        className="text-primary hover:underline"
                        data-testid="resource-organizer-link"
                        onClick={() => {
                          if (r.organizer.target_type && r.organizer.target_id) {
                            trackInternalLink("resource", r.id, "organizer",
                              r.organizer.target_type, r.organizer.target_id);
                          }
                        }}
                      >
                        View profile
                      </Link>
                    )}
                  </div>
                </div>
              </Card>
            )}

            <PublicQna
              targetType="resource"
              targetId={r.id}
              ask={!r.is_owner ? <AskQuestionButton targetType="resource" targetId={r.id} targetName={r.title} /> : null}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
